import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  NativeSelect,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import CloseIcon from '@mui/icons-material/Close';
import cronstrue from 'cronstrue';

const CATEGORY_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'sales', label: 'Sales' },
  { value: 'labor', label: 'Labor' },
  { value: 'tips', label: 'Tips' },
  { value: 'cogs', label: 'COGS' },
  { value: 'other', label: 'Other' },
];

const TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'UTC', label: 'UTC' },
];

const PLATFORM_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'snowflake', label: 'Snowflake' },
  { value: 'vm', label: 'Virtual Machine' },
  { value: 'powerbi', label: 'Power BI' },
];

const CompactInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #dde1e6',
    fontSize: 13,
    padding: '6px 10px',
    fontFamily: 'Lato, sans-serif',
    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const EMPTY_FORM = {
  name: '',
  description: '',
  scheduleCron: '',
  scheduleTimezone: 'America/Los_Angeles',
  procedureName: '',
  procedureArgs: '',
  categoryKey: '',
  sourceKey: '',
  platformType: '',
  platformHost: '',
};

const AutomationFormDialog = ({ open, onClose, onSave, automation, saving }) => {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [cronPreview, setCronPreview] = useState('');
  const [cronError, setCronError] = useState('');

  useEffect(() => {
    if (open) {
      if (automation) {
        setForm({
          name: automation.name || '',
          description: automation.description || '',
          scheduleCron: automation.scheduleCron || '',
          scheduleTimezone: automation.scheduleTimezone || 'America/Los_Angeles',
          procedureName: automation.procedureName || '',
          procedureArgs: automation.procedureArgs || '',
          categoryKey: automation.categoryKey || '',
          sourceKey: automation.sourceKey || '',
          platformType: automation.platformType || '',
          platformHost: automation.platformHost || '',
        });
      } else {
        setForm({ ...EMPTY_FORM });
      }
    }
  }, [open, automation]);

  useEffect(() => {
    if (form.scheduleCron) {
      try {
        const readable = cronstrue.toString(form.scheduleCron, { use24HourTimeFormat: false });
        setCronPreview(readable);
        setCronError('');
      } catch {
        setCronPreview('');
        setCronError('Invalid cron expression');
      }
    } else {
      setCronPreview('');
      setCronError('');
    }
  }, [form.scheduleCron]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSave(form);
  };

  const canSave = form.name && form.scheduleCron && !cronError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontFamily: 'Lato',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {automation ? 'Edit Automation' : 'Add Automation'}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            size="small"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Description"
            size="small"
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            fullWidth
            multiline
            rows={2}
          />

          <TextField
            label="Schedule (Cron)"
            size="small"
            value={form.scheduleCron}
            onChange={(e) => handleChange('scheduleCron', e.target.value)}
            placeholder="0 6 * * *"
            helperText={
              cronError ? (
                <span style={{ color: '#d32f2f' }}>{cronError}</span>
              ) : cronPreview ? (
                <span style={{ color: '#2e7d32' }}>{cronPreview}</span>
              ) : (
                'e.g., "0 6 * * *" = Every day at 6:00 AM'
              )
            }
            fullWidth
            required
          />

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Timezone
            </Typography>
            <NativeSelect
              value={form.scheduleTimezone}
              onChange={(e) => handleChange('scheduleTimezone', e.target.value)}
              input={<CompactInput />}
              fullWidth
            >
              {TIMEZONE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Category (optional)
            </Typography>
            <NativeSelect
              value={form.categoryKey}
              onChange={(e) => handleChange('categoryKey', e.target.value)}
              input={<CompactInput />}
              fullWidth
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Platform (optional)
            </Typography>
            <NativeSelect
              value={form.platformType}
              onChange={(e) => handleChange('platformType', e.target.value)}
              input={<CompactInput />}
              fullWidth
            >
              {PLATFORM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect>
          </Box>

          {form.platformType === 'vm' && (
            <TextField
              label="VM Host"
              size="small"
              value={form.platformHost}
              onChange={(e) => handleChange('platformHost', e.target.value)}
              helperText="Hostname or IP of the virtual machine"
              fullWidth
            />
          )}

          {form.platformType === 'powerbi' && (
            <TextField
              label="Report/Workspace"
              size="small"
              value={form.platformHost}
              onChange={(e) => handleChange('platformHost', e.target.value)}
              helperText="Power BI report or workspace name"
              fullWidth
            />
          )}

          <TextField
            label="Source Key (optional)"
            size="small"
            value={form.sourceKey}
            onChange={(e) => handleChange('sourceKey', e.target.value)}
            helperText="Link to a specific data source key"
            fullWidth
          />

          <TextField
            label="Procedure Name (optional)"
            size="small"
            value={form.procedureName}
            onChange={(e) => handleChange('procedureName', e.target.value)}
            helperText="Snowflake procedure that runs"
            fullWidth
          />

          {form.procedureName && (
            <TextField
              label="Procedure Args"
              size="small"
              value={form.procedureArgs}
              onChange={(e) => handleChange('procedureArgs', e.target.value)}
              helperText="Template tokens: {START_DATE}, {END_DATE}, {STORE_ID}"
              fullWidth
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: 'none', fontFamily: 'Lato' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSave || saving}
          sx={{ textTransform: 'none', fontFamily: 'Lato' }}
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutomationFormDialog;
